/**
 * Command Registry - Schema-validated command type catalog.
 *
 * All command types registered in Nuvra are listed here with:
 *   - Their payload schema (structural validation at dispatch time)
 *   - Access tier (which actor roles may issue this command)
 *   - Whether audit is required
 *   - Whether approval is required before execution
 *
 * The registry is read-only at runtime; all mutations require a server-side
 * configuration reload. This prevents dynamic privilege escalation.
 */

// ── Types ─────────────────────────────────────────────────────────────────────

export type CommandAccessTier = "owner" | "admin" | "builder" | "viewer" | "system";

export interface CommandSchema {
  commandType:      string;
  description:      string;
  accessTiers:      CommandAccessTier[];
  requiresAudit:    boolean;
  requiresApproval: boolean;
  /** Validate payload shape. Return null on pass, error message on fail. */
  validatePayload:  (payload: unknown) => string | null;
}

// ── Payload validators ────────────────────────────────────────────────────────

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function requireStrings(v: unknown, ...keys: string[]): string | null {
  if (!isRecord(v)) return "payload must be an object";
  for (const k of keys) {
    if (typeof (v as Record<string, unknown>)[k] !== "string" || !(v as Record<string, unknown>)[k]) {
      return `payload.${k} must be a non-empty string`;
    }
  }
  return null;
}

// ── Catalog ───────────────────────────────────────────────────────────────────

const COMMAND_CATALOG: CommandSchema[] = [
  // Project commands
  {
    commandType:      "project:create",
    description:      "Create a new project workspace",
    accessTiers:      ["owner", "admin", "builder"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "name"),
  },
  {
    commandType:      "project:delete",
    description:      "Permanently delete a project",
    accessTiers:      ["owner", "admin"],
    requiresAudit:    true,
    requiresApproval: true,
    validatePayload:  (p) => requireStrings(p, "projectId"),
  },
  {
    commandType:      "project:publish",
    description:      "Publish the current project build",
    accessTiers:      ["owner", "admin", "builder"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "projectId"),
  },
  {
    commandType:      "project:unpublish",
    description:      "Unpublish a live project",
    accessTiers:      ["owner", "admin"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "projectId"),
  },

  // Build commands
  {
    commandType:      "build:start",
    description:      "Start a build from a prompt",
    accessTiers:      ["owner", "admin", "builder"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "projectId", "prompt"),
  },
  {
    commandType:      "build:cancel",
    description:      "Cancel an in-progress build",
    accessTiers:      ["owner", "admin", "builder"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "buildId"),
  },
  {
    commandType:      "build:retry",
    description:      "Retry a failed build",
    accessTiers:      ["owner", "admin", "builder"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "buildId"),
  },

  // Schema commands
  {
    commandType:      "schema:lock_section",
    description:      "Lock a schema section against AI modification",
    accessTiers:      ["owner", "admin"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "projectId", "sectionId"),
  },
  {
    commandType:      "schema:unlock_section",
    description:      "Unlock a previously locked schema section",
    accessTiers:      ["owner", "admin"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "projectId", "sectionId"),
  },
  {
    commandType:      "schema:hide_page",
    description:      "Hide a page from the generated app output",
    accessTiers:      ["owner", "admin", "builder"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "projectId", "pageId"),
  },
  {
    commandType:      "schema:show_page",
    description:      "Restore a previously hidden page",
    accessTiers:      ["owner", "admin", "builder"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "projectId", "pageId"),
  },

  // Deploy commands
  {
    commandType:      "deploy:to_vercel",
    description:      "Deploy the current build artifact to Vercel",
    accessTiers:      ["owner", "admin"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "projectId"),
  },
  {
    commandType:      "deploy:to_netlify",
    description:      "Deploy the current build artifact to Netlify",
    accessTiers:      ["owner", "admin"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "projectId"),
  },
  {
    commandType:      "deploy:rollback",
    description:      "Roll back to a prior deployment version",
    accessTiers:      ["owner", "admin"],
    requiresAudit:    true,
    requiresApproval: true,
    validatePayload:  (p) => requireStrings(p, "projectId", "targetDeployId"),
  },

  // User commands
  {
    commandType:      "user:invite",
    description:      "Invite a user to the org",
    accessTiers:      ["owner", "admin"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "email"),
  },
  {
    commandType:      "user:remove",
    description:      "Remove a user from the org",
    accessTiers:      ["owner", "admin"],
    requiresAudit:    true,
    requiresApproval: true,
    validatePayload:  (p) => requireStrings(p, "userId"),
  },

  // Agent commands
  {
    commandType:      "agent:spawn",
    description:      "Spawn an agent for autonomous work",
    accessTiers:      ["system"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "agentId", "goal"),
  },
  {
    commandType:      "agent:terminate",
    description:      "Terminate a running agent",
    accessTiers:      ["owner", "admin", "system"],
    requiresAudit:    true,
    requiresApproval: false,
    validatePayload:  (p) => requireStrings(p, "agentId"),
  },

  // System commands
  {
    commandType:      "system:kernel_halt",
    description:      "Halt the runtime kernel",
    accessTiers:      ["system"],
    requiresAudit:    true,
    requiresApproval: true,
    validatePayload:  (p) => requireStrings(p, "reason"),
  },
  {
    commandType:      "system:kernel_restart",
    description:      "Restart the runtime kernel",
    accessTiers:      ["owner", "system"],
    requiresAudit:    true,
    requiresApproval: true,
    validatePayload:  (p) => requireStrings(p, "reason"),
  },
];

// ── Registry ──────────────────────────────────────────────────────────────────

class CommandRegistryImpl {
  private catalog = new Map<string, CommandSchema>(
    COMMAND_CATALOG.map(s => [s.commandType, s]),
  );

  /** Get the schema for a command type. Returns null if unknown. */
  get(commandType: string): CommandSchema | null {
    return this.catalog.get(commandType) ?? null;
  }

  /** Validate a payload against the schema. Returns null on success. */
  validate(commandType: string, payload: unknown): string | null {
    const schema = this.catalog.get(commandType);
    if (!schema) return `Unknown command type: "${commandType}"`;
    return schema.validatePayload(payload);
  }

  /** True if the given access tier may issue this command type. */
  isAuthorized(commandType: string, tier: CommandAccessTier): boolean {
    const schema = this.catalog.get(commandType);
    if (!schema) return false;
    return schema.accessTiers.includes(tier);
  }

  /** True if this command type requires human approval before execution. */
  requiresApproval(commandType: string): boolean {
    return this.catalog.get(commandType)?.requiresApproval ?? false;
  }

  /** True if this command type requires audit trail entry. */
  requiresAudit(commandType: string): boolean {
    return this.catalog.get(commandType)?.requiresAudit ?? true;
  }

  /** All registered command types. */
  allTypes(): string[] { return [...this.catalog.keys()]; }

  /** All command schemas accessible to the given tier. */
  forTier(tier: CommandAccessTier): CommandSchema[] {
    return [...this.catalog.values()].filter(s => s.accessTiers.includes(tier));
  }

  /** Register an additional command type (for plugin/extension use). */
  register(schema: CommandSchema): void {
    if (this.catalog.has(schema.commandType)) {
      throw new Error(`Command type "${schema.commandType}" is already registered in the catalog.`);
    }
    this.catalog.set(schema.commandType, schema);
  }
}

export const commandRegistry = new CommandRegistryImpl();
